import * as R from "remeda";
import { BotProvider, useGetApiBotProviderGet, useGetApiBotProviderStatisticsGetByBotProviderId, useGetApiWorkspaceGet, usePutApiBotProviderUpdate } from "@/API"
import { Button, Divider, Group, Stack, Text } from "@mantine/core"
import { useParams } from "@tanstack/react-router"
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { BotConfigEditButton } from "./Components/BotConfigEditButton";
import { TextProcessConfigEditButton } from "./Components/TextProcessConfigEditButton";
import { ChatHandlerConfigEditBotton } from "./Components/ChatHandlerConfigEditBotton";
import { LogChatCountsData, LogTokenUsageData, SumChatCountsData, SumTokenUsageData } from "@/Helpers/TokenUsageHelper";
import { TooltipMultiline } from "@/Components/TooltipMultiline";
import { ChatBotDataLink } from "@/Workspaces/Components/ChatBotDataLink";
import { ChatBotDataIdEditButton } from "./Components/ChatBotDataIdEditButton";
import { IconBrandLine, IconLetterCase, IconMessage, IconMessageCog, IconRobot, IconSparkles } from "@tabler/icons-react";
import { LineBotProviderDataCard } from "./Line/LineBotProviderDataCard";
import { LineBotProviderDataEditButton } from "./Line/LineBotProviderDataEdit";
import { WebBotProviderDataCard } from "./Web/WebBotProviderDataCard";
import { WebBotProviderDataEditButton } from "./Web/WebBotProviderDataEdit";
import { WebBotCustomUiButton } from "./Web/CustomUi/WebBotCustomUiModal";
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs";

export function BotProviderPage() {
    const { workspaceId, botProviderId } = useParams({ from: "/workspace/$workspaceId/botProvider/$botProviderId/" })
    const queryClient = useQueryClient()
    const workspace = useGetApiWorkspaceGet({ id: workspaceId })
    const botProviderGet = useGetApiBotProviderGet({ workspaceId: workspaceId, id: botProviderId })
    const botProvider = botProviderGet.data?.data ?? {}

    const Statistics = useGetApiBotProviderStatisticsGetByBotProviderId({ workspaceId: workspaceId, botProviderId: botProviderId })
    const chatCount = SumChatCountsData(Statistics.data?.data.chatCounts ?? {})
    const chatCountLog = LogChatCountsData(Statistics.data?.data.chatCounts ?? {})
    const tokenUsage = SumTokenUsageData(Statistics.data?.data.tokenUsage ?? {})
    const tokenUsageLog = LogTokenUsageData(Statistics.data?.data.tokenUsage ?? {})

    const update_Mutation = usePutApiBotProviderUpdate()

    async function UpdateData(changeFunc: (data: BotProvider) => void) {
        try {
            var result = await botProviderGet.refetch()
            if (result.data?.data) {
                var data = R.clone(result.data?.data)
                changeFunc(data)
                await update_Mutation.mutateAsync({
                    params: {
                        workspaceId: workspaceId,
                    },
                    data: data
                })
            }
            queryClient.invalidateQueries({ queryKey: botProviderGet.queryKey })
            notifications.show({ message: "更新成功" })
        } catch (e) {
            notifications.show({ message: "更新失敗", color: "red" })
        }
    }
    return <>
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId } },
                },
                {
                    label: '機器人串接',
                    linkProps: { to: '/workspace/$workspaceId/botProvider', params: { workspaceId } },
                },
                {
                    label: botProvider.name ? botProvider.name : '串接資訊',
                    active: true,
                },
            ]}
        />
        <Group>
            {
                botProvider.type == "Line" && <LineBotProviderDataEditButton initValue={botProvider.providerData} onChange={async (v) => {
                    UpdateData((data) => {
                        data.providerData = v
                    })
                }}>
                    <Button leftSection={<IconBrandLine />}>
                        修改LINE連接設定
                    </Button>
                </LineBotProviderDataEditButton>
            }
            {
                botProvider.type == "Web" && <WebBotProviderDataEditButton workspaceId={workspaceId} botProviderId={botProviderId} initValue={botProvider.providerData} onChange={async (v) => {
                    UpdateData((data) => {
                        data.providerData = v
                    })
                }}>
                    <Button leftSection={<IconBrandLine />}>
                        修改Web設定
                    </Button>
                </WebBotProviderDataEditButton>
            }
            {
                botProvider.type == "Web" && <WebBotCustomUiButton
                    workspaceId={workspaceId}
                    botProviderId={botProviderId}
                    botProvider={botProvider}
                    onUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: botProviderGet.queryKey })
                    }}
                >
                    <Button leftSection={<IconSparkles />} color="violet" variant="light">
                        自訂 UI 設定
                    </Button>
                </WebBotCustomUiButton>
            }
            <ChatBotDataIdEditButton workspaceId={workspaceId} value={botProvider.chatBotDataId ?? ""} onChange={async (v) => {
                UpdateData((data) => {
                    data.chatBotDataId = v
                })
            }}>
                <Button leftSection={<IconRobot />}>
                    修改連接機器人
                </Button>
            </ChatBotDataIdEditButton>
            <BotConfigEditButton value={botProvider.botConfig} onChange={async (v) => {
                UpdateData((data) => {
                    data.botConfig = v
                })
            }}>
                <Button leftSection={<IconMessage />}>
                    回應設定
                </Button>
            </BotConfigEditButton>
            <TextProcessConfigEditButton value={botProvider.textProcessConfig} onChange={async (v) => {
                UpdateData((data) => {
                    data.textProcessConfig = v
                })
            }}>
                <Button leftSection={<IconLetterCase />}>
                    文字後處理設定
                </Button>
            </TextProcessConfigEditButton>
            <ChatHandlerConfigEditBotton workspaceId={workspaceId} value={botProvider.userChatHandlerConfigs} onChange={async (v) => {
                UpdateData((data) => {
                    data.userChatHandlerConfigs = v
                })
            }}>
                <Button leftSection={<IconMessageCog />}>
                    聊天處理設定
                </Button>
            </ChatHandlerConfigEditBotton>
        </Group>
        <Stack gap={0} mt="sm">
            <Group gap={5}>
                <Text>連接機器人:</Text>
                <ChatBotDataLink workspaceId={botProvider.workspaceId ?? ""} chatBotDataId={botProvider.chatBotDataId ?? ""} />
            </Group>
            <TooltipMultiline label={chatCountLog}>
                <Text>
                    已使用聊天點數:{chatCount}
                </Text>
            </TooltipMultiline>
            <TooltipMultiline label={tokenUsageLog}>
                <Text>
                    Input/Output Tokens:{tokenUsage.inputTokens ?? 0}/{tokenUsage.outputTokens ?? 0}
                </Text>
            </TooltipMultiline>
        </Stack>
        <Divider my="sm" />
        {
            botProvider.type == "Line" && <LineBotProviderDataCard botProvider={botProvider} />
        }
        {
            botProvider.type == "Web" && <WebBotProviderDataCard botProvider={botProvider} />
        }
    </>
}